import { useAuth } from "@/lib/auth";
import { getEnvMap } from "@/lib/network";
import { createHttpAgent, formatCandidError } from "@motokomaker/ic-client";
import { Actor, type ActorSubclass } from "@icp-sdk/core/agent";
import { IDL } from "@icp-sdk/core/candid";
import { Principal } from "@icp-sdk/core/principal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMemo, useState } from "react";

type DemoTokenId = "ICP" | "ckBTC" | "ckETH" | "ckUSDC" | "MMK";

const TOKEN_OPTS: { id: DemoTokenId; label: string; defaultAmt: string }[] = [
	{ id: "ICP", label: "ICP", defaultAmt: "10" },
	{ id: "ckBTC", label: "ckBTC", defaultAmt: "0.01" },
	{ id: "ckETH", label: "ckETH", defaultAmt: "0.1" },
	{ id: "ckUSDC", label: "ckUSDC", defaultAmt: "100" },
	{ id: "MMK", label: "MMK", defaultAmt: "1000" },
];

const faucetIdlFactory: IDL.InterfaceFactory = ({ IDL: I }) => {
	const DemoToken = I.Variant({
		ICP: I.Null,
		ckBTC: I.Null,
		ckETH: I.Null,
		ckUSDC: I.Null,
		MMK: I.Null,
	});
	const MintError = I.Variant({
		AnonymousCaller: I.Null,
		InvalidAmount: I.Null,
		RateLimitExceeded: I.Record({
			token: DemoToken,
			dailyCapE8: I.Nat,
			usedE8: I.Nat,
		}),
		LifetimeUsdCapExceeded: I.Record({
			maxUsdE8: I.Nat,
			usedUsdE8: I.Nat,
			claimUsdE8: I.Nat,
		}),
		PriceUnavailable: I.Text,
		Ledger: I.Text,
		Protocol: I.Text,
	});
	const MintResult = I.Variant({
		ok: I.Null,
		err: MintError,
	});
	return I.Service({
		mint: I.Func([DemoToken, I.Nat], [MintResult], []),
	});
};

type FaucetService = {
	mint: (
		token: Record<string, null>,
		amount: bigint,
	) => Promise<{ ok?: null; err?: unknown }>;
};

function tokenVariant(id: DemoTokenId): Record<string, null> {
	return { [id]: null };
}

function amountToE8(amount: number): bigint {
	return BigInt(Math.round(amount * 1e8));
}

export function demoFaucetConfigured(): boolean {
	const env = getEnvMap();
	return Boolean(
		env.VITE_CANISTER_ID_DEMO_FAUCET || env.VITE_DEMO_FAUCET_CANISTER_ID,
	);
}

/** Local / Play demo faucet mint — only when faucet canister ID is in env. */
export function DemoFaucetPanel() {
	const { identity, isAuthenticated, refreshBalances } = useAuth();
	const qc = useQueryClient();
	const [token, setToken] = useState<DemoTokenId>("ICP");
	const [amount, setAmount] = useState("10");
	const faucetId = useMemo(() => {
		const env = getEnvMap();
		return (
			env.VITE_CANISTER_ID_DEMO_FAUCET ||
			env.VITE_DEMO_FAUCET_CANISTER_ID ||
			""
		).trim();
	}, []);

	const mint = useMutation({
		mutationFn: async () => {
			if (!identity || !faucetId) throw new Error("Faucet not available");
			const n = Number(amount);
			if (!Number.isFinite(n) || n <= 0) throw new Error("Invalid amount");
			const config = (await import("@/lib/network")).getIcConfig();
			const agent = await createHttpAgent(config, identity);
			const faucet = Actor.createActor(faucetIdlFactory, {
				agent,
				canisterId: Principal.fromText(faucetId),
			}) as ActorSubclass<FaucetService>;
			const result = await faucet.mint(tokenVariant(token), amountToE8(n));
			if (result && "err" in result && result.err != null) {
				throw new Error(`Faucet: ${formatCandidError(result.err)}`);
			}
		},
		onSuccess: () => {
			toast.success(`Minted ${amount} ${token} (demo faucet)`);
			void refreshBalances();
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
		},
		onError: (e: Error) => toast.error(e.message),
	});

	if (!faucetId) return null;

	return (
		<div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
			<div>
				<p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
					Demo faucet
				</p>
				<p className="mt-1 text-[11px] text-muted-foreground">
					Local / Play only — mints demo collaterals &amp; MMK to your II
					principal. Not real value.
				</p>
			</div>
			<div className="flex flex-wrap gap-2">
				{TOKEN_OPTS.map((t) => (
					<button
						key={t.id}
						type="button"
						onClick={() => {
							setToken(t.id);
							setAmount(t.defaultAmt);
						}}
						className={
							token === t.id
								? "rounded-full bg-amber-500/30 px-3 py-1 text-xs font-semibold"
								: "rounded-full border border-border px-3 py-1 text-xs"
						}
					>
						{t.label}
					</button>
				))}
			</div>
			<div className="flex gap-2">
				<input
					value={amount}
					onChange={(e) => setAmount(e.target.value)}
					className="h-10 flex-1 rounded-xl border border-border bg-background px-3 font-mono text-sm"
					inputMode="decimal"
				/>
				<button
					type="button"
					disabled={!isAuthenticated || mint.isPending}
					onClick={() => mint.mutate()}
					className="h-10 rounded-xl bg-amber-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
				>
					{mint.isPending ? "Minting…" : "Mint"}
				</button>
			</div>
			{!isAuthenticated ? (
				<p className="text-[11px] text-muted-foreground">Connect II to mint.</p>
			) : null}
		</div>
	);
}
